import { Breadcrumb, Button, Form, Input, Table, Tag } from "antd";
import "./report.scss";
import { FileExcelOutlined, HomeOutlined, PrinterOutlined, SearchOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import { DatePicker as DatePickerJalali, JalaliLocaleListener } from "antd-jalali";
import { Excel } from "antd-table-saveas-excel";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { _adr } from "../../../address";
import { NotFound } from "../../../sources/svg/svg";
import { convertToGregorian, validateInput } from "../../../tools/tools";
import Loading from "../loader/loading";
import { useLayout } from "../../layouts/layout";

const PAGE_SIZE = 10;

const toGregorian = (d) => convertToGregorian(d.format("YYYY-MM-DD"), "-", "-");

// status text from the API -> pill colour (plain grey when nothing matches)
const STATUS_TONES = [
	[/ناقص|رد شد|لغو|خطا|ناموفق/, "error"],
	[/انتظار|بررسی|در حال/, "warning"],
	[/نهایی|موفق|فعال|تکمیل|انجام شد/, "success"],
];
export const statusTone = (text) => STATUS_TONES.find(([re]) => re.test(text ?? ""))?.[1];
export const StatusTag = ({ text }) =>
	text ? (
		<Tag color={statusTone(text)} className="report-status">
			{text}
		</Tag>
	) : (
		<span className="report-muted">—</span>
	);

// small "print document" action used inside report rows
export const PrintButton = ({ onClick }) => (
	<Button size="small" icon={<PrinterOutlined />} onClick={onClick} className="report-print-btn">
		چاپ سند
	</Button>
);

// column shorthand used by the report pages
// { wrap: true } lets a long text column (e.g. package name) break onto several lines
export const col = (title, dataIndex, { wrap, ...extra } = {}) => ({
	title,
	dataIndex,
	key: dataIndex,
	...(wrap && { className: "report-wrap" }),
	...extra,
});

// renders a document into the layout's print slot, then opens the browser print dialog (print / save as PDF)
export const usePrintDocument = () => {
	const { setLayout } = useLayout();
	const [busy, setBusy] = useState(false);
	// build: async () => <Document /> | null
	const print = async (build) => {
		setBusy(true);
		try {
			const doc = await build();
			if (doc) {
				setLayout(doc);
				await new Promise((resolve) => setTimeout(resolve, 1000)); // let the document render (images, fonts)
				window.print();
			}
		} finally {
			setBusy(false);
		}
	};
	return { busy, print };
};

// date-range form; onSearch gets [from, to] (antd-jalali dates)
export const RangeFilter = ({ onSearch, loading = false, pickerProps }) => {
	const [t] = useTranslation("global");
	return (
		<Form onFinish={({ duringdate }) => onSearch([...duringdate])} /* new array: searching the same range again reloads */ layout="vertical" className="report-filters">
			<JalaliLocaleListener />
			<Form.Item name="duringdate" label="بازه تاریخ" rules={[validateInput({ t: t, validate: true, typeOfValue: "array" })]}>
				{/* always open below: flipping up got clipped at the top of the screen */}
				<DatePickerJalali.RangePicker dropdownAlign={{ overflow: { adjustY: 0 } }} format={"YYYY/MM/DD"} {...pickerProps} />
			</Form.Item>
			<Button type="primary" htmlType="submit" icon={<SearchOutlined />} className="report-search-btn" loading={loading}>
				جستجو
			</Button>
		</Form>
	);
};

/**
 * Server-paged results of one search, with in-table search and Excel export.
 * Must live inside a `.report-page` container (styles).
 *
 * range      [from, to] dates of the search; a new array (new search) reloads from page 1
 * query      (payload) => response; payload = { startDate, endDate, search: { page, entriesperpage, ... } }
 * columns    antd columns; a column with `excel: false` is left out of the Excel file (e.g. a print button)
 * toRow      (item) => plain row values (strings / numbers) — rendering belongs in column.render
 * excel      { sheet, file }
 * title      heading above the table
 * busy       extra loading flag from the page (e.g. while building a print document)
 */
export const ReportResults = ({ range, query, columns, toRow, excel, title = "نتایج", busy = false }) => {
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [rows, setRows] = useState([]);
	const [filter, setFilter] = useState("");

	const load = async (nextPage) => {
		setLoading(true);
		try {
			const response = await query({
				startDate: toGregorian(range[0]),
				endDate: toGregorian(range[1]),
				search: { sort: "", filter: "", entriesperpage: PAGE_SIZE, page: nextPage - 1, totalrowcount: 0 },
			});
			if (response) {
				setRows((response.Result ?? []).map((item, key) => ({ key, ...toRow(item), _raw: item })));
				setTotal(response.searchconfigmodel?.totalrowcount ?? 0);
				setPage(nextPage);
			}
		} catch (error) {
			console.error(error);
		}
		setLoading(false);
	};

	// a new search always starts from the first page
	useEffect(() => {
		load(1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [range]);

	const term = filter.trim().toLowerCase();
	const visible = term
		? rows.filter((r) =>
				Object.entries(r).some(
					([k, v]) =>
						k !== "_raw" &&
						k !== "key" &&
						String(v ?? "")
							.toLowerCase()
							.includes(term),
				),
			)
		: rows;

	const handleExport = () => {
		const exportColumns = columns.filter((c) => c.excel !== false).map(({ title, dataIndex, key }) => ({ title, dataIndex, key }));
		new Excel()
			.addSheet(excel.sheet)
			.addColumns(exportColumns)
			.addDataSource(
				visible.map(({ _raw, ...r }) => r),
				{ str2Percent: true },
			)
			.saveAs(excel.file);
	};

	// status cells become a coloured pill unless the page renders them itself
	const tableColumns = columns.map((c) => (c.dataIndex === "status" && !c.render ? { ...c, render: (v) => <StatusTag text={v} /> } : c));

	return (
		<section className="report-table">
			<header>
				<div className="report-table-title">
					<h4>{title}</h4>
					<span className="report-muted">{total} مورد</span>
				</div>
				<div className="report-table-tools">
					<Input placeholder="جستجو..." allowClear value={filter} onChange={(e) => setFilter(e.target.value)} prefix={<SearchOutlined />} className="report-filter" />
					<Button onClick={handleExport} icon={<FileExcelOutlined />} disabled={!visible.length}>
						خروجی اکسل
					</Button>
				</div>
			</header>
			<Table
				className="tbl-normal"
				columns={tableColumns}
				dataSource={visible}
				scroll={{ x: true }}
				locale={{ emptyText: <NotFound text={"رکوردی یافت نشد."} /> }}
				pagination={{
					pageSize: PAGE_SIZE,
					align: "start",
					current: page,
					total,
					showSizeChanger: false,
					hideOnSinglePage: true,
					onChange: load,
				}}
			/>
			<Loading isOpen={loading || busy} />
		</section>
	);
};

export const REPORT_HINT = <p className="report-hint">بازه تاریخ را انتخاب کنید و «جستجو» را بزنید.</p>;

/**
 * Date-range report page: title, filter and results.
 *
 * title      page title and breadcrumb
 * prefix     legacy class prefix ("sp" -> container-sp, sp-container) so existing page styles keep applying
 * pickerProps extra props for the date range picker
 * the rest goes to ReportResults
 */
const ReportPage = ({ title, prefix, pickerProps, busy = false, ...results }) => {
	const [range, setRange] = useState(null); // null = nothing searched yet

	return (
		<div className={`container-main container-${prefix}`}>
			<div className="g-hold-title-of-page">
				<Breadcrumb className="breadcrumbs" items={[{ href: _adr.Root + _adr.Home, title: <HomeOutlined /> }, { title }]} />
				<div className="title-page">
					<h3>{title}</h3>
				</div>
			</div>
			<div className="g-layout-content">
				<Content className={`g-layout-main report-page ${prefix}-container`}>
					<RangeFilter onSearch={setRange} pickerProps={pickerProps} />
					{range ? <ReportResults range={range} busy={busy} {...results} /> : REPORT_HINT}
				</Content>
			</div>
		</div>
	);
};

export default ReportPage;
